// components/CartContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart debe ser usado dentro de un CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [cartCount, setCartCount] = useState(0);

  // Cargar carrito desde localStorage al inicializar
  useEffect(() => {
    const loadCart = () => {
      try {
        const savedCart = localStorage.getItem('carrito');
        if (savedCart) {
          const cartData = JSON.parse(savedCart);
          setCart(cartData);
          updateCartCount(cartData);
        }
      } catch (error) {
        console.error('Error cargando carrito:', error);
        setCart([]);
        setCartCount(0);
      }
    };

    loadCart();
  }, []);

  // Actualizar contador
  const updateCartCount = (cartData) => {
    const totalItems = cartData.reduce((total, item) => total + item.quantity, 0);
    setCartCount(totalItems);
  };

  // 🔥 FUNCIÓN CORREGIDA: Añadir producto al carrito con cantidad correcta
  const addToCart = (product) => {
    console.log('🛒 CartContext - Añadiendo producto:', {
      nombre: product.name,
      id: product.id,
      cantidadRecibida: product.quantity,
      tipo: product.type
    });

    const existingItemIndex = cart.findIndex(item => {
      // Para variaciones, comparar por ID de variación
      if (product.type === 'variation') {
        return item.id === product.id;
      }
      // Para productos simples, comparar por ID y atributos
      return item.id === product.id && JSON.stringify(item.attributes) === JSON.stringify(product.attributes);
    });
    
    let newCart;
    
    if (existingItemIndex !== -1) {
      // 🔥 CORREGIDO: Sumar la cantidad recibida en lugar de solo +1
      const existingItem = cart[existingItemIndex];
      const newQuantity = existingItem.quantity + (product.quantity || 1);
      
      newCart = cart.map((item, index) => 
        index === existingItemIndex 
          ? { ...item, quantity: newQuantity }
          : item
      );
      
      console.log('🛒 Producto existente - Nueva cantidad:', newQuantity);
    } else {
      // 🔥 CORREGIDO: Usar la cantidad recibida en lugar de siempre 1
      const newItem = {
        id: product.id,
        parent_id: product.parent_id || product.id,
        name: product.product_name || product.name,
        price: parseFloat(product.price),
        sale_price: product.sale_price ? parseFloat(product.sale_price) : null,
        regular_price: product.regular_price ? parseFloat(product.regular_price) : parseFloat(product.price),
        image: product.image || (product.images && product.images[0] ? product.images[0].src : null),
        quantity: product.quantity || 1, // 🔥 USAR LA CANTIDAD RECIBIDA
        stock_status: product.stock_status,
        sku: product.sku || '',
        categories: product.categories || [],
        type: product.type || 'simple',
        attributes: product.attributes || []
      };
      newCart = [...cart, newItem];
      
      console.log('🛒 Nuevo producto - Cantidad:', newItem.quantity);
    }

    setCart(newCart);
    updateCartCount(newCart);
    localStorage.setItem('carrito', JSON.stringify(newCart));
    
    console.log('🛒 Carrito actualizado:', newCart);
  };

  // Eliminar producto del carrito
  const removeFromCart = (productId) => {
    const newCart = cart.filter(item => item.id !== productId);
    setCart(newCart);
    updateCartCount(newCart);
    localStorage.setItem('carrito', JSON.stringify(newCart));
  };

  // Disminuir cantidad
  const decreaseQuantity = (productId) => {
    const existingItemIndex = cart.findIndex(item => item.id === productId);
    
    if (existingItemIndex !== -1) {
      const existingItem = cart[existingItemIndex];
      
      if (existingItem.quantity > 1) {
        const newCart = cart.map((item, index) => 
          index === existingItemIndex 
            ? { ...item, quantity: item.quantity - 1 }
            : item
        );
        setCart(newCart);
        updateCartCount(newCart);
        localStorage.setItem('carrito', JSON.stringify(newCart));
      } else {
        removeFromCart(productId);
      }
    }
  };

  // Aumentar cantidad
  const increaseQuantity = (productId) => {
    const existingItemIndex = cart.findIndex(item => item.id === productId);
    
    if (existingItemIndex !== -1) {
      const newCart = cart.map((item, index) => 
        index === existingItemIndex 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      );
      setCart(newCart);
      updateCartCount(newCart);
      localStorage.setItem('carrito', JSON.stringify(newCart));
    }
  };

  // Vaciar carrito
  const clearCart = () => {
    setCart([]);
    setCartCount(0);
    localStorage.removeItem('carrito');
  };

  // Obtener el total del carrito
  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      const price = item.sale_price || item.price;
      return total + (price * item.quantity);
    }, 0);
  };

  // Obtener información de envío e impuestos
  const getCartSummary = () => {
    const subtotal = getCartTotal();
    const shipping = subtotal > 50 ? 0 : 5.99; // Envío gratis sobre 50€
    const tax = subtotal * 0.21; // IVA 21%
    const finalTotal = subtotal + shipping + tax;

    return {
      subtotal,
      shipping,
      tax,
      finalTotal
    };
  };

  // Verificar si un producto está en el carrito
  const isInCart = (productId) => {
    return cart.some(item => item.id === productId);
  };

  // Obtener cantidad de un producto específico
  const getProductQuantity = (productId) => {
    const item = cart.find(item => item.id === productId);
    return item ? item.quantity : 0;
  };

  const value = {
    cart,
    cartCount,
    addToCart,
    removeFromCart,
    decreaseQuantity,
    increaseQuantity,
    clearCart,
    updateCartCount,
    getCartTotal,
    getCartSummary,
    isInCart,
    getProductQuantity
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};